import React, { ReactNode, FC } from 'react'
import classNames from 'classnames'
import { Box, makeStyles, Typography, BoxProps, createStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'

interface IEldIntegrationStepContainerProps {
  children: ReactNode
  title?: ReactNode
  subtitle?: ReactNode
  /**
   * @example 'Page 1 of 2'
   */
  pageText?: ReactNode
  footer?: ReactNode
  /**
   * @default 'center'
   */
  textAlign?: BoxProps['textAlign']
  /**
   * @default 647
   */
  maxWidth?: number
  /**
   * @default false
   */
  fadeIn?: boolean
}

type TClassKey = 'root' | 'subtitle' | 'footer' | 'inner'

const useEldIntegrationStepContainerStyles = makeStyles(theme =>
  createStyles<TClassKey, Pick<IEldIntegrationStepContainerProps, 'maxWidth'>>({
    root: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    inner: {
      flex: '1 1 auto',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(3),
      margin: '0 auto',
      overflowY: 'scroll',

      // Add more padding on desktop
      [theme.breakpoints.up('md')]: {
        maxWidth: props => props.maxWidth,
        paddingTop: theme.spacing(9),
      },
    },
    subtitle: {
      marginTop: theme.spacing(1),
      color: brandColors.coolGray8,
    },
    footer: {
      borderTop: `1px solid ${brandColors.coolGray4}`,
      padding: theme.spacing(3),
      textAlign: 'right',
    },
  })
)

/**
 * Helper display component to style each step in ELD integration flow
 */
export const EldIntegrationStepContainer: FC<IEldIntegrationStepContainerProps> = ({
  children,
  fadeIn,
  footer,
  maxWidth = 647,
  pageText,
  subtitle,
  textAlign = 'left',
  title,
}) => {
  const classes = useEldIntegrationStepContainerStyles({
    maxWidth,
  })
  return (
    <Box
      className={classNames(classes.root, {
        fadeInUp: fadeIn,
      })}
      data-test='eld-integration-step-container'>
      <Box className={classes.inner}>
        <Box textAlign={textAlign}>
          {pageText && (
            <Box pb={2}>
              <Typography variant='body1' component='div'>
                {pageText}
              </Typography>
            </Box>
          )}
          {title && (
            <Typography variant='h3' component='div'>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant='body1' component='div' className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box pt={5}>{children}</Box>
      </Box>
      <Box className={classes.footer} data-test='eld-integration-step-container-footer'>
        {footer}
      </Box>
    </Box>
  )
}
